import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Collapse,
    Nav,
} from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import SEO from '@/components/common/SEO';
import InfoTooltip from '@/components/common/InfoTooltip';
import {
    getReportedPosts,
    getReportedReplies,
    getPendingReviewPosts,
    getPendingReviewReplies,
    dismissReportsByPost,
    dismissReportsByReply,
    moderatePost,
    moderateReply,
    REPORT_REASONS,
} from '@/shared/services/reportApi';
import { banMember, getMemberships } from '@/shared/services/membershipApi';
import { getCommunities } from '@/shared/services/communityApi';
import ModeratorMembers from '@/features/moderator/ModeratorMembers';
import { deleteReply } from '@/shared/services/replyApi';

import './admin-ui.css';
import './AdminDashboard.css';
import './ModerationAdmin.css';

const reasonLabel = (value) => {
    const found = REPORT_REASONS.find((r) => r.value === value);
    return found ? found.label : value;
};

const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const truncate = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

const formatPostType = (type) => {
    if (!type) return 'Discussion';
    return type
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default function ModerationAdmin() {
    const { user } = useAuth();
    const api = useAuthenticatedAxios();

    const [reportedPosts, setReportedPosts] = useState([]);
    const [pendingPosts, setPendingPosts] = useState([]);
    const [pendingTotal, setPendingTotal] = useState(0);
    const [pendingReplies, setPendingReplies] = useState([]);
    const [pendingRepliesTotal, setPendingRepliesTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [pendingRepliesLoading, setPendingRepliesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingError, setPendingError] = useState(null);
    const [pendingRepliesError, setPendingRepliesError] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(null);
    const [expandedPost, setExpandedPost] = useState(null);
    const [activeTab, setActiveTab] = useState('moderation');
    const [communities, setCommunities] = useState([]);
    const [reportedReplies, setReportedReplies] = useState([]);
    const [repliesReportedLoading, setRepliesReportedLoading] = useState(true);
    const [repliesReportedError, setRepliesReportedError] = useState(null);
    const [reportsTabs, setReportsTabs] = useState('posts');
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        postId: null,
        action: null,
        postTitle: '',
        postAuthor: '',
        authorId: null,
        communityId: null,
        reason: '',
        source: null, // 'reported' | 'pending' | 'pending-reply'
    });

    const fetchReports = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            setError(null);
            const data = await getReportedPosts(api);
            setReportedPosts(data);
        } catch (err) {
            console.error('Failed to fetch reported posts:', err);
            setError(err.response?.data?.message || 'Failed to load reports');
        } finally {
            setLoading(false);
        }
    }, [user, api]);

    const fetchReplyReports = useCallback(async () => {
        if (!user) return;
        try {
            setRepliesReportedLoading(true);
            setRepliesReportedError(null);
            const data = await getReportedReplies(api);
            setReportedReplies(data);
        } catch (err) {
            console.error('Failed to fetch reported posts:', err);
            setError(err.response?.data?.message || 'Failed to load reports');
        } finally {
            setRepliesReportedLoading(false);
        }
    }, [user, api]);

    const fetchCommunities = useCallback(async () => {
        try {
            setError(null);
            const data = await getCommunities({}, api);
            setCommunities(
                data.map((c) => ({
                    communityId: c._id,
                    community: { name: c.name },
                }))
            );
        } catch (err) {
            console.error('Failed to fetch communities:', err);
            setError(
                err.response?.data?.message || 'Failed to load communities'
            );
        }
    }, [api]);

    const fetchPendingPosts = useCallback(async () => {
        if (!user) return;
        try {
            setPendingLoading(true);
            setPendingError(null);
            // Fetch up to 100 posts (server cap) so the full queue is visible.
            // Pagination UI can be added later if queues regularly exceed 100.
            const data = await getPendingReviewPosts(api, 1, 100);
            setPendingPosts(data.posts || []);
            setPendingTotal(data.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch pending review posts:', err);
            setPendingError(
                err.response?.data?.message || 'Failed to load pending posts'
            );
        } finally {
            setPendingLoading(false);
        }
    }, [user, api]);

    const fetchPendingReplies = useCallback(async () => {
        if (!user) return;
        try {
            setPendingRepliesLoading(true);
            setPendingRepliesError(null);
            const data = await getPendingReviewReplies(api, 1, 100);
            setPendingReplies(data.replies || []);
            setPendingRepliesTotal(data.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch pending review replies:', err);
            setPendingRepliesError(
                err.response?.data?.message || 'Failed to load pending replies'
            );
        } finally {
            setPendingRepliesLoading(false);
        }
    }, [user, api]);

    useEffect(() => {
        fetchReports();
        fetchReplyReports();
        fetchPendingPosts();
        fetchPendingReplies();
        fetchCommunities();
    }, [
        fetchReports,
        fetchReplyReports,
        fetchPendingPosts,
        fetchPendingReplies,
        fetchCommunities,
    ]);

    const handleDismissAll = async (postId) => {
        setActionInProgress(postId);
        try {
            await dismissReportsByPost(api, postId);
            setReportedPosts((prev) =>
                prev.filter((item) => item._id.toString() !== postId.toString())
            );
        } catch (err) {
            console.error('Failed to dismiss reports:', err);
            setError(
                err.response?.data?.message || 'Failed to dismiss reports'
            );
        } finally {
            setActionInProgress(null);
        }
    };

    const handleDismissAllReplies = async (postId) => {
        setActionInProgress(postId);
        try {
            await dismissReportsByReply(api, postId);
            setReportedReplies((prev) =>
                prev.filter((item) => item._id.toString() !== postId.toString())
            );
        } catch (err) {
            console.error('Failed to dismiss reports:', err);
            setError(
                err.response?.data?.message || 'Failed to dismiss reports'
            );
        } finally {
            setActionInProgress(null);
        }
    };

    const handleModerate = async () => {
        const { postId, action, source } = confirmModal;
        setConfirmModal({
            show: false,
            postId: null,
            action: null,
            postTitle: '',
            postAuthor: '',
            authorId: null,
            communityId: null,
            reason: '',
            source: null,
        });
        setActionInProgress(postId);
        try {
            if (source === 'pending-reply') {
                await moderateReply(api, postId, action);
                setPendingReplies((prev) =>
                    prev.filter((r) => r._id.toString() !== postId.toString())
                );
                setPendingRepliesTotal((prev) => Math.max(prev - 1, 0));
            } else if (source === 'pending') {
                await moderatePost(api, postId, action);
                setPendingPosts((prev) =>
                    prev.filter(
                        (post) => post._id.toString() !== postId.toString()
                    )
                );
                setPendingTotal((prev) => Math.max(prev - 1, 0));
            } else if (source === 'reply-report') {
                await deleteReply(api, postId);
                setReportedReplies((prev) =>
                    prev.filter(
                        (item) => item._id.toString() !== postId.toString()
                    )
                );
                if (action === 'ban') {
                    const memberships = await getMemberships(
                        {
                            communityId: confirmModal.communityId,
                            userId: confirmModal.authorId,
                            status: 'active',
                        },
                        api
                    );
                    const membership = memberships[0];
                    if (!membership) {
                        setError(
                            'Reply deleted, but user has no active membership to ban.'
                        ); // This error message might have to be deleted
                        return; // depending if we decide to continue to let users make replies without an account
                    }
                    await banMember(api, membership._id, confirmModal.reason);
                }
            } else {
                await moderatePost(
                    api,
                    postId,
                    action === 'ban' ? 'delete' : action
                );
                setReportedPosts((prev) =>
                    prev.filter(
                        (item) => item._id.toString() !== postId.toString()
                    )
                );
                if (action === 'ban') {
                    const memberships = await getMemberships(
                        {
                            communityId: confirmModal.communityId,
                            userId: confirmModal.authorId,
                            status: 'active',
                        },
                        api
                    );

                    const membership = memberships[0];

                    if (!membership) {
                        setError(
                            'Post deleted, but the user has no active membership to ban.'
                        );
                        return;
                    }

                    await banMember(api, membership._id, confirmModal.reason);
                }
            }
        } catch (err) {
            console.error('Failed to moderate:', err);
            setError(err.response?.data?.message || 'Failed to moderate');
        } finally {
            setActionInProgress(null);
        }
    };

    const openConfirmModal = ({
        postId,
        action,
        postTitle = '',
        postAuthor = '',
        authorId = null,
        communityId = null,
        reason = '',
        source = 'reported',
    }) => {
        setConfirmModal({
            show: true,
            postId,
            action,
            postTitle,
            postAuthor,
            authorId,
            communityId,
            reason,
            source,
        });
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            show: false,
            postId: null,
            action: null,
            postTitle: '',
            postAuthor: '',
            authorId: null,
            communityId: null,
            reason: '',
            source: null,
        });
    };

    const isPendingDecision =
        confirmModal.source === 'pending' ||
        confirmModal.source === 'pending-reply';
    const isReply = confirmModal.source === 'pending-reply';
    const isReplyReport = confirmModal.source === 'reply-report';
    const contentNoun = isReplyReport ? 'reply' : 'post';
    const ContentNoun = isReplyReport ? 'Reply' : 'Post';
    const pendingConfirmConfig =
        confirmModal.action === 'approve'
            ? {
                  tone: 'approve',
                  icon: 'bi-check2-circle',
                  eyebrow: 'Pending review decision',
                  title: isReply ? 'Publish this reply?' : 'Publish this post?',
                  message: isReply
                      ? 'Approving moves this reply out of review and makes it visible under its post.'
                      : 'Approving moves this post out of review and makes it visible in its community.',
                  actionLabel: 'Approve and Publish',
                  buttonVariant: 'success',
                  consequences: [
                      'Visible to community members immediately',
                      'Removed from the pending review queue',
                  ],
              }
            : {
                  tone: 'reject',
                  icon: 'bi-x-octagon',
                  eyebrow: 'Pending review decision',
                  title: isReply ? 'Reject this reply?' : 'Reject this post?',
                  message: isReply
                      ? 'Rejecting removes this reply from the queue and marks it as deleted.'
                      : 'Rejecting removes this post from the queue and marks it as deleted.',
                  actionLabel: isReply ? 'Reject Reply' : 'Reject Post',
                  buttonVariant: 'danger',
                  consequences: isReply
                      ? [
                            'Reply will not appear under the post',
                            'Removed from the pending review queue',
                        ]
                      : [
                            'Post will not appear in public community views',
                            'Community post count will be updated',
                        ],
              };

    return (
        <main>
            <SEO title="Admin: Moderation" />
            <div className="bg-white moderation-subtabs">
                <Nav variant="underline" className="justify-content-center">
                    <Nav.Item>
                        <Nav.Link
                            active={activeTab === 'moderation'}
                            onClick={() => setActiveTab('moderation')}
                        >
                            <i className="bi bi-flag me-2" aria-hidden="true" />
                            Moderation
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            active={activeTab === 'members'}
                            onClick={() => setActiveTab('members')}
                        >
                            <i
                                className="bi bi-people me-2"
                                aria-hidden="true"
                            />
                            Members
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </div>
            <Container className="py-4">
                <h1 className="fs-3 fw-bold text-white mb-4">Moderation</h1>
                {error && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p className="mb-2">{error}</p>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={fetchReports}
                        >
                            Try Again
                        </Button>
                    </Alert>
                )}

                {activeTab === 'members' && (
                    <ModeratorMembers communities={communities} />
                )}

                {activeTab === 'moderation' && (
                    <>
                        <div className="mb-5">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <h2 className="fs-5 fw-semibold text-white mb-0">
                                    Pending Review
                                </h2>
                                {pendingTotal > 0 && (
                                    <Badge bg="warning" text="dark" pill>
                                        {pendingTotal}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-white-50 small mb-3">
                                Posts held for human review because the
                                moderation engine was unreachable when they were
                                submitted.
                            </p>

                            {pendingLoading && (
                                <div className="d-flex justify-content-center p-4">
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        size="sm"
                                    />
                                    <span className="visually-hidden">
                                        Loading pending posts...
                                    </span>
                                </div>
                            )}

                            {!pendingLoading && pendingError && (
                                <Alert
                                    variant="danger"
                                    dismissible
                                    onClose={() => setPendingError(null)}
                                >
                                    <Alert.Heading>
                                        Something went wrong
                                    </Alert.Heading>
                                    <p className="mb-2">{pendingError}</p>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={fetchPendingPosts}
                                    >
                                        Try Again
                                    </Button>
                                </Alert>
                            )}

                            {!pendingLoading &&
                                !pendingError &&
                                pendingPosts.length === 0 && (
                                    <Alert variant="success">
                                        No posts pending review. Queue is clear!
                                    </Alert>
                                )}

                            {!pendingLoading &&
                                !pendingError &&
                                pendingPosts.length > 0 && (
                                    <div className="d-grid gap-3">
                                        {pendingPosts.map((post) => {
                                            const postTitle =
                                                post.title || 'Untitled Post';
                                            const isBusy =
                                                actionInProgress === post._id;

                                            return (
                                                <Card
                                                    key={post._id}
                                                    className="pending-review-card shadow-sm overflow-hidden rounded-3"
                                                >
                                                    <Card.Body className="pending-review-card-body d-grid">
                                                        <div className="pending-review-main">
                                                            <div className="pending-review-kicker d-flex flex-wrap align-items-center gap-2 justify-content-md-between">
                                                                <span className="pending-review-status d-inline-flex align-items-center fw-bold text-uppercase">
                                                                    <i
                                                                        className="bi bi-hourglass-split"
                                                                        aria-hidden="true"
                                                                    />
                                                                    Needs review
                                                                </span>
                                                                <Badge
                                                                    bg="light"
                                                                    text="dark"
                                                                    className="border fw-semibold"
                                                                >
                                                                    {formatPostType(
                                                                        post.postType
                                                                    )}
                                                                </Badge>
                                                            </div>

                                                            <h3 className="pending-review-title fw-bold">
                                                                {postTitle}
                                                            </h3>

                                                            <div className="pending-review-meta d-flex flex-wrap align-items-center gap-2">
                                                                <span>
                                                                    <i
                                                                        className="bi bi-person-circle"
                                                                        aria-hidden="true"
                                                                    />
                                                                    {post.authorName ||
                                                                        'Unknown author'}
                                                                </span>
                                                                {post.createdAt && (
                                                                    <span>
                                                                        <i
                                                                            className="bi bi-calendar3"
                                                                            aria-hidden="true"
                                                                        />
                                                                        {formatDate(
                                                                            post.createdAt
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="pending-review-content border rounded-3 bg-body-tertiary">
                                                                {truncate(
                                                                    post.content,
                                                                    280
                                                                ) || (
                                                                    <span className="text-body-secondary">
                                                                        No post
                                                                        content
                                                                        provided.
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {post.resourceUrl && (
                                                                <div className="pending-review-resource d-flex align-items-center">
                                                                    <i
                                                                        className="bi bi-link-45deg"
                                                                        aria-hidden="true"
                                                                    />
                                                                    <a
                                                                        href={
                                                                            post.resourceUrl
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {
                                                                            post.resourceUrl
                                                                        }
                                                                    </a>
                                                                    {post.resourceType && (
                                                                        <Badge
                                                                            bg="light"
                                                                            text="dark"
                                                                            className="ms-2"
                                                                        >
                                                                            {
                                                                                post.resourceType
                                                                            }
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className="pending-review-community d-flex flex-wrap align-items-center gap-2">
                                                                <span className="pending-review-community-name d-inline-flex align-items-center fw-semibold">
                                                                    <i
                                                                        className="bi bi-people"
                                                                        aria-hidden="true"
                                                                    />
                                                                    {post.communityName ||
                                                                        'Unknown Community'}
                                                                </span>
                                                                {post.communityCategory && (
                                                                    <Badge
                                                                        bg="success"
                                                                        className="pending-review-category"
                                                                    >
                                                                        {
                                                                            post.communityCategory
                                                                        }
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="pending-review-actions d-grid d-md-flex flex-md-column align-items-md-center justify-content-md-start gap-2">
                                                            <Button
                                                                variant="success"
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                onClick={() =>
                                                                    openConfirmModal(
                                                                        {
                                                                            postId: post._id,
                                                                            action: 'approve',
                                                                            postTitle,
                                                                            source: 'pending',
                                                                        }
                                                                    )
                                                                }
                                                                aria-label={`Approve ${postTitle}`}
                                                            >
                                                                {isBusy ? (
                                                                    <Spinner
                                                                        animation="border"
                                                                        size="sm"
                                                                        className="me-2"
                                                                    />
                                                                ) : (
                                                                    <i
                                                                        className="bi bi-check2-circle me-2"
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                onClick={() =>
                                                                    openConfirmModal(
                                                                        {
                                                                            postId: post._id,
                                                                            action: 'delete',
                                                                            postTitle,
                                                                            source: 'pending',
                                                                        }
                                                                    )
                                                                }
                                                                aria-label={`Reject ${postTitle}`}
                                                            >
                                                                <i
                                                                    className="bi bi-x-circle me-2"
                                                                    aria-hidden="true"
                                                                />
                                                                Reject
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                        </div>

                        {/* Pending Replies */}
                        <div className="mb-5">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <h2 className="fs-5 fw-semibold text-white mb-0">
                                    Pending Replies
                                </h2>
                                {pendingRepliesTotal > 0 && (
                                    <Badge bg="warning" text="dark" pill>
                                        {pendingRepliesTotal}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-white-50 small mb-3">
                                Replies held for human review because the
                                moderation engine was unreachable when they were
                                submitted.
                            </p>

                            {pendingRepliesLoading && (
                                <div className="d-flex justify-content-center p-4">
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        size="sm"
                                    />
                                    <span className="visually-hidden">
                                        Loading pending replies...
                                    </span>
                                </div>
                            )}

                            {!pendingRepliesLoading && pendingRepliesError && (
                                <Alert
                                    variant="danger"
                                    dismissible
                                    onClose={() => setPendingRepliesError(null)}
                                >
                                    <Alert.Heading>
                                        Something went wrong
                                    </Alert.Heading>
                                    <p className="mb-2">
                                        {pendingRepliesError}
                                    </p>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={fetchPendingReplies}
                                    >
                                        Try Again
                                    </Button>
                                </Alert>
                            )}

                            {!pendingRepliesLoading &&
                                !pendingRepliesError &&
                                pendingReplies.length === 0 && (
                                    <Alert variant="success">
                                        No replies pending review. Queue is
                                        clear!
                                    </Alert>
                                )}

                            {!pendingRepliesLoading &&
                                !pendingRepliesError &&
                                pendingReplies.length > 0 && (
                                    <div className="d-grid gap-3">
                                        {pendingReplies.map((reply) => {
                                            const replyPreview =
                                                reply.content || 'No content';
                                            const isBusy =
                                                actionInProgress === reply._id;

                                            return (
                                                <Card
                                                    key={reply._id}
                                                    className="pending-review-card shadow-sm overflow-hidden rounded-3"
                                                >
                                                    <Card.Body className="pending-review-card-body d-grid">
                                                        <div className="pending-review-main">
                                                            <div className="pending-review-kicker d-flex flex-wrap align-items-center gap-2 justify-content-md-between">
                                                                <span className="pending-review-status d-inline-flex align-items-center fw-bold text-uppercase">
                                                                    <i
                                                                        className="bi bi-hourglass-split"
                                                                        aria-hidden="true"
                                                                    />
                                                                    Needs review
                                                                </span>
                                                                <Badge
                                                                    bg="light"
                                                                    text="dark"
                                                                    className="border fw-semibold"
                                                                >
                                                                    Reply
                                                                </Badge>
                                                            </div>

                                                            <h3 className="visually-hidden">
                                                                Reply by{' '}
                                                                {reply.authorName ||
                                                                    'Unknown author'}
                                                            </h3>

                                                            <div className="pending-review-meta d-flex flex-wrap align-items-center gap-2">
                                                                <span>
                                                                    <i
                                                                        className="bi bi-person-circle"
                                                                        aria-hidden="true"
                                                                    />
                                                                    {reply.authorName ||
                                                                        'Unknown author'}
                                                                </span>
                                                                {reply.createdAt && (
                                                                    <span>
                                                                        <i
                                                                            className="bi bi-calendar3"
                                                                            aria-hidden="true"
                                                                        />
                                                                        {formatDate(
                                                                            reply.createdAt
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="pending-review-content border rounded-3 bg-body-tertiary">
                                                                {truncate(
                                                                    replyPreview,
                                                                    280
                                                                )}
                                                            </div>

                                                            {reply.postTitle && (
                                                                <div className="pending-review-community d-flex flex-wrap align-items-center gap-2">
                                                                    <span className="pending-review-community-name d-inline-flex align-items-center fw-semibold">
                                                                        <i
                                                                            className="bi bi-chat-left-text"
                                                                            aria-hidden="true"
                                                                        />
                                                                        {
                                                                            reply.postTitle
                                                                        }
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {reply.communityName && (
                                                                <div className="pending-review-community d-flex flex-wrap align-items-center gap-2">
                                                                    <span className="pending-review-community-name d-inline-flex align-items-center fw-semibold">
                                                                        <i
                                                                            className="bi bi-people"
                                                                            aria-hidden="true"
                                                                        />
                                                                        {
                                                                            reply.communityName
                                                                        }
                                                                    </span>
                                                                    {reply.communityCategory && (
                                                                        <Badge
                                                                            bg="success"
                                                                            className="pending-review-category"
                                                                        >
                                                                            {
                                                                                reply.communityCategory
                                                                            }
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="pending-review-actions d-grid d-md-flex flex-md-column align-items-md-center justify-content-md-start gap-2">
                                                            <Button
                                                                variant="success"
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                onClick={() =>
                                                                    openConfirmModal(
                                                                        {
                                                                            postId: reply._id,
                                                                            action: 'approve',
                                                                            postTitle:
                                                                                truncate(
                                                                                    reply.content,
                                                                                    60
                                                                                ),
                                                                            source: 'pending-reply',
                                                                        }
                                                                    )
                                                                }
                                                                aria-label={`Approve reply by ${reply.authorName}`}
                                                            >
                                                                {isBusy ? (
                                                                    <Spinner
                                                                        animation="border"
                                                                        size="sm"
                                                                        className="me-2"
                                                                    />
                                                                ) : (
                                                                    <i
                                                                        className="bi bi-check2-circle me-2"
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                onClick={() =>
                                                                    openConfirmModal(
                                                                        {
                                                                            postId: reply._id,
                                                                            action: 'delete',
                                                                            postTitle:
                                                                                truncate(
                                                                                    reply.content,
                                                                                    60
                                                                                ),
                                                                            source: 'pending-reply',
                                                                        }
                                                                    )
                                                                }
                                                                aria-label={`Reject reply by ${reply.authorName}`}
                                                            >
                                                                <i
                                                                    className="bi bi-x-circle me-2"
                                                                    aria-hidden="true"
                                                                />
                                                                Reject
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                        </div>
                        <div className="bg-white moderation-subtabs mt-4 mb-4">
                            <Nav
                                variant="underline"
                                className="justify-content-center"
                            >
                                <Nav.Item>
                                    <Nav.Link
                                        active={reportsTabs === 'posts'}
                                        onClick={() => setReportsTabs('posts')}
                                    >
                                        <i
                                            className="bi bi-file-post me-2"
                                            aria-hidden="true"
                                        />
                                        Post Reports
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link
                                        active={reportsTabs === 'replies'}
                                        onClick={() =>
                                            setReportsTabs('replies')
                                        }
                                    >
                                        <i
                                            className="bi bi-chat-square-text me-2"
                                            aria-hidden="true"
                                        />
                                        Reply Reports
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </div>

                        {reportsTabs === 'posts' && (
                            <>
                                <div className="d-flex align-items-center gap-3 mb-4">
                                    <h2 className="fs-5 fw-semibold text-white mb-0">
                                        Reported Posts
                                    </h2>
                                    {/* Outside the h2 so the aria-label is not
                                        folded into the heading name. */}
                                    <InfoTooltip
                                        variant="light"
                                        text="Dismiss clears every report on a post and leaves the post published. Hide and Delete ask you to confirm and explain what they do before they take effect."
                                    />
                                    {reportedPosts.length > 0 && (
                                        <Badge bg="danger" pill>
                                            {reportedPosts.length}
                                        </Badge>
                                    )}
                                </div>

                                {loading && (
                                    <div className="d-flex justify-content-center p-4">
                                        <Spinner
                                            animation="border"
                                            variant="light"
                                            size="sm"
                                        />
                                        <span className="visually-hidden">
                                            Loading reports...
                                        </span>
                                    </div>
                                )}

                                {!loading &&
                                    !error &&
                                    reportedPosts.length === 0 && (
                                        <Alert variant="info">
                                            No pending reports. All clear!
                                        </Alert>
                                    )}

                                {!loading &&
                                    reportedPosts.map((item) => (
                                        <Card
                                            key={item._id}
                                            className="mb-3 shadow-sm"
                                        >
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="flex-grow-1 me-3">
                                                        <h3 className="fs-5 fw-semibold mb-1">
                                                            {item.post?.title ||
                                                                'Untitled Post'}
                                                        </h3>
                                                        <p className="text-body-secondary mb-1 small">
                                                            by{' '}
                                                            <span className="fw-medium">
                                                                {item.post
                                                                    ?.authorName ||
                                                                    'Unknown'}
                                                            </span>
                                                            {item.post
                                                                ?.createdAt && (
                                                                <>
                                                                    {' '}
                                                                    &middot;{' '}
                                                                    {formatDate(
                                                                        item
                                                                            .post
                                                                            .createdAt
                                                                    )}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                    <Badge
                                                        bg="danger"
                                                        className="fs-6"
                                                    >
                                                        {item.reportCount}{' '}
                                                        {item.reportCount === 1
                                                            ? 'report'
                                                            : 'reports'}
                                                    </Badge>
                                                </div>

                                                <p className="text-body mb-3">
                                                    {truncate(
                                                        item.post?.content
                                                    )}
                                                </p>

                                                <div className="d-flex flex-wrap gap-2 mb-3">
                                                    {item.reasons.map(
                                                        (reason) => (
                                                            <Badge
                                                                key={reason}
                                                                bg="warning"
                                                                text="dark"
                                                            >
                                                                {reasonLabel(
                                                                    reason
                                                                )}
                                                            </Badge>
                                                        )
                                                    )}
                                                </div>

                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="mb-3"
                                                    onClick={() =>
                                                        setExpandedPost(
                                                            expandedPost ===
                                                                item._id
                                                                ? null
                                                                : item._id
                                                        )
                                                    }
                                                    aria-expanded={
                                                        expandedPost ===
                                                        item._id
                                                    }
                                                    aria-controls={`reports-detail-${item._id}`}
                                                    aria-label={`${expandedPost === item._id ? 'Hide' : 'Show'} report details for ${item.post?.title || 'this post'}`}
                                                >
                                                    {expandedPost === item._id
                                                        ? 'Hide details'
                                                        : 'Show report details'}
                                                </Button>

                                                <Collapse
                                                    in={
                                                        expandedPost ===
                                                        item._id
                                                    }
                                                >
                                                    <div
                                                        id={`reports-detail-${item._id}`}
                                                    >
                                                        <div className="border rounded p-3 bg-light mb-3">
                                                            {item.reports.map(
                                                                (report) => (
                                                                    <div
                                                                        key={
                                                                            report._id
                                                                        }
                                                                        className="mb-3 pb-3 border-bottom report-detail-item"
                                                                    >
                                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                                            <span className="fw-medium small">
                                                                                {
                                                                                    report.reporterName
                                                                                }
                                                                            </span>
                                                                            <span className="text-secondary small">
                                                                                {formatDate(
                                                                                    report.createdAt
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <Badge
                                                                            bg="warning"
                                                                            text="dark"
                                                                            className="mb-1"
                                                                        >
                                                                            {reasonLabel(
                                                                                report.reason
                                                                            )}
                                                                        </Badge>
                                                                        {report.comments && (
                                                                            <p className="text-body small mb-0 mt-1">
                                                                                &ldquo;
                                                                                {
                                                                                    report.comments
                                                                                }
                                                                                &rdquo;
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                </Collapse>

                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        className="text-white"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            handleDismissAll(
                                                                item._id
                                                            )
                                                        }
                                                        aria-label={`Dismiss all reports for ${item.post?.title || 'this post'}`}
                                                    >
                                                        {actionInProgress ===
                                                            item._id && (
                                                            <Spinner
                                                                animation="border"
                                                                size="sm"
                                                                className="me-1"
                                                            />
                                                        )}
                                                        Dismiss
                                                    </Button>
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        className="text-dark"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            openConfirmModal({
                                                                postId: item._id,
                                                                action: 'hide',
                                                                postTitle:
                                                                    item.post
                                                                        ?.title ||
                                                                    'Untitled Post',
                                                            })
                                                        }
                                                        aria-label={`Hide ${item.post?.title || 'this post'}`}
                                                    >
                                                        Hide Post
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            openConfirmModal({
                                                                postId: item._id,
                                                                action: 'delete',
                                                                postTitle:
                                                                    item.post
                                                                        ?.title ||
                                                                    'Untitled Post',
                                                            })
                                                        }
                                                        aria-label={`Delete ${item.post?.title || 'this post'}`}
                                                    >
                                                        Delete Post
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            openConfirmModal({
                                                                postId: item._id,
                                                                action: 'ban',
                                                                postTitle:
                                                                    item.post
                                                                        ?.title ||
                                                                    'Untitled Post',
                                                                postAuthor:
                                                                    item.post
                                                                        ?.authorName ||
                                                                    'Unknown Author',
                                                                authorId:
                                                                    item.post
                                                                        ?.authorId,
                                                                communityId:
                                                                    item.post
                                                                        ?.communityId,
                                                            })
                                                        }
                                                        aria-label={`Delete ${item.post?.title || 'this post'} & ban ${item.post?.authorName || 'its author'}`}
                                                    >
                                                        Delete Post & Ban User
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                            </>
                        )}

                        {reportsTabs === 'replies' && (
                            <>
                                <div className="d-flex align-items-center gap-3 mb-4">
                                    <h2 className="fs-5 fw-semibold text-white mb-0">
                                        Reported Replies
                                    </h2>
                                    <InfoTooltip
                                        variant="light"
                                        text="Dismiss clears every report on a reply and leaves the reply published.
                                         Hide and Delete ask you to confirm and explain what they do before they take effect."
                                    />
                                    {reportedReplies.length > 0 && (
                                        <Badge bg="danger" pill>
                                            {reportedReplies.length}
                                        </Badge>
                                    )}
                                </div>

                                {repliesReportedLoading && (
                                    <div className="d-flex justify-content-center p-4">
                                        <Spinner
                                            animation="border"
                                            variant="light"
                                            size="sm"
                                        />
                                        <span className="visually-hidden">
                                            Loading reply reports...
                                        </span>
                                    </div>
                                )}

                                {!repliesReportedLoading &&
                                    repliesReportedError && (
                                        <Alert variant="danger">
                                            {repliesReportedError}
                                        </Alert>
                                    )}

                                {!repliesReportedLoading &&
                                    !repliesReportedError &&
                                    reportedReplies.length === 0 && (
                                        <Alert variant="info">
                                            No pending reply reports. All clear!
                                        </Alert>
                                    )}

                                {!repliesReportedLoading &&
                                    reportedReplies.map((item) => (
                                        <Card
                                            key={item._id}
                                            className="mb-3 shadow-sm"
                                        >
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="flex-grow-1 me-3">
                                                        <h3 className="fs-6 fw-semibold mb-1">
                                                            Reply by{' '}
                                                            {item.reply
                                                                ?.authorName ||
                                                                'Unknown'}
                                                        </h3>
                                                        {item.reply
                                                            ?.createdAt && (
                                                            <p className="text-body-secondary mb-1 small">
                                                                {formatDate(
                                                                    item.reply
                                                                        .createdAt
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Badge
                                                        bg="danger"
                                                        className="fs-6"
                                                    >
                                                        {item.reportCount}{' '}
                                                        {item.reportCount === 1
                                                            ? 'report'
                                                            : 'reports'}
                                                    </Badge>
                                                </div>

                                                <p className="text-body mb-3">
                                                    {truncate(
                                                        item.reply?.content
                                                    )}
                                                </p>

                                                <div className="d-flex flex-wrap gap-2 mb-3">
                                                    {item.reasons.map(
                                                        (reason) => (
                                                            <Badge
                                                                key={reason}
                                                                bg="warning"
                                                                text="dark"
                                                            >
                                                                {reasonLabel(
                                                                    reason
                                                                )}
                                                            </Badge>
                                                        )
                                                    )}
                                                </div>

                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="mb-3"
                                                    onClick={() =>
                                                        setExpandedPost(
                                                            expandedPost ===
                                                                item._id
                                                                ? null
                                                                : item._id
                                                        )
                                                    }
                                                    aria-expanded={
                                                        expandedPost ===
                                                        item._id
                                                    }
                                                    aria-controls={`reply-reports-detail-${item._id}`}
                                                >
                                                    {expandedPost === item._id
                                                        ? 'Hide details'
                                                        : 'Show report details'}
                                                </Button>

                                                <Collapse
                                                    in={
                                                        expandedPost ===
                                                        item._id
                                                    }
                                                >
                                                    <div
                                                        id={`reply-reports-detail-${item._id}`}
                                                    >
                                                        <div className="border rounded p-3 bg-light mb-3">
                                                            {item.reports.map(
                                                                (report) => (
                                                                    <div
                                                                        key={
                                                                            report._id
                                                                        }
                                                                        className="mb-3 pb-3 border-bottom report-detail-item"
                                                                    >
                                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                                            <span className="fw-medium small">
                                                                                {
                                                                                    report.reporterName
                                                                                }
                                                                            </span>
                                                                            <span className="text-secondary small">
                                                                                {formatDate(
                                                                                    report.createdAt
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <Badge
                                                                            bg="warning"
                                                                            text="dark"
                                                                            className="mb-1"
                                                                        >
                                                                            {reasonLabel(
                                                                                report.reason
                                                                            )}
                                                                        </Badge>
                                                                        {report.comments && (
                                                                            <p className="text-body small mb-0 mt-1">
                                                                                &ldquo;
                                                                                {
                                                                                    report.comments
                                                                                }
                                                                                &rdquo;
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                </Collapse>

                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        className="text-white"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            handleDismissAllReplies(
                                                                item._id
                                                            )
                                                        }
                                                    >
                                                        {actionInProgress ===
                                                            item._id && (
                                                            <Spinner
                                                                animation="border"
                                                                size="sm"
                                                                className="me-1"
                                                            />
                                                        )}
                                                        Dismiss
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            openConfirmModal({
                                                                postId: item._id,
                                                                action: 'delete',
                                                                postTitle:
                                                                    truncate(
                                                                        item
                                                                            .reply
                                                                            ?.content,
                                                                        60
                                                                    ) ||
                                                                    'this reply',
                                                                source: 'reply-report',
                                                            })
                                                        }
                                                    >
                                                        Delete Reply
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={
                                                            actionInProgress ===
                                                            item._id
                                                        }
                                                        onClick={() =>
                                                            openConfirmModal({
                                                                postId: item._id,
                                                                action: 'ban',
                                                                postTitle:
                                                                    truncate(
                                                                        item
                                                                            .reply
                                                                            ?.content,
                                                                        60
                                                                    ) ||
                                                                    'this reply',
                                                                postAuthor:
                                                                    item.reply
                                                                        ?.authorName ||
                                                                    'Unknown Author',
                                                                authorId:
                                                                    item.reply
                                                                        ?.authorId,
                                                                communityId:
                                                                    item.communityId,
                                                                source: 'reply-report',
                                                            })
                                                        }
                                                    >
                                                        Delete Reply & Ban User
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                            </>
                        )}

                        <Modal
                            show={confirmModal.show}
                            onHide={closeConfirmModal}
                            centered
                            aria-labelledby="confirm-modal-title"
                            className={
                                isPendingDecision
                                    ? 'moderation-confirm-modal'
                                    : ''
                            }
                        >
                            {isPendingDecision ? (
                                <>
                                    <Modal.Header
                                        closeButton
                                        className="border-0 pb-0"
                                    >
                                        <Modal.Title
                                            id="confirm-modal-title"
                                            className="visually-hidden"
                                        >
                                            {pendingConfirmConfig.title}
                                        </Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body className="pt-0">
                                        <div
                                            className={`moderation-confirm-hero moderation-confirm-hero-${pendingConfirmConfig.tone} d-grid`}
                                        >
                                            <div className="moderation-confirm-icon d-inline-flex align-items-center justify-content-center rounded-circle text-white">
                                                <i
                                                    className={`bi ${pendingConfirmConfig.icon}`}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="moderation-confirm-copy">
                                                <p className="moderation-confirm-eyebrow fw-bold text-uppercase">
                                                    {
                                                        pendingConfirmConfig.eyebrow
                                                    }
                                                </p>
                                                <h3>
                                                    {pendingConfirmConfig.title}
                                                </h3>
                                                <p>
                                                    {
                                                        pendingConfirmConfig.message
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="moderation-confirm-post d-grid border rounded-3 bg-body-tertiary gap-1">
                                            <span>
                                                {isReply ? 'Reply' : 'Post'}
                                            </span>
                                            <strong>
                                                &ldquo;{confirmModal.postTitle}
                                                &rdquo;
                                            </strong>
                                        </div>

                                        <div className="moderation-confirm-effects d-grid">
                                            {pendingConfirmConfig.consequences.map(
                                                (item) => (
                                                    <span key={item}>
                                                        <i
                                                            className="bi bi-dot"
                                                            aria-hidden="true"
                                                        />
                                                        {item}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </Modal.Body>
                                    <Modal.Footer className="border-0 pt-0 moderation-confirm-footer d-grid">
                                        <Button
                                            variant="outline-primary"
                                            onClick={closeConfirmModal}
                                        >
                                            Keep Reviewing
                                        </Button>
                                        <Button
                                            variant={
                                                pendingConfirmConfig.buttonVariant
                                            }
                                            onClick={handleModerate}
                                        >
                                            <i
                                                className={`bi ${pendingConfirmConfig.icon} me-2`}
                                                aria-hidden="true"
                                            />
                                            {pendingConfirmConfig.actionLabel}
                                        </Button>
                                    </Modal.Footer>
                                </>
                            ) : (
                                <>
                                    <Modal.Header closeButton>
                                        <Modal.Title
                                            id="confirm-modal-title"
                                            className="fs-5"
                                        >
                                            Confirm{' '}
                                            {confirmModal.action === 'hide'
                                                ? 'Hide'
                                                : 'Delete'}{' '}
                                            {ContentNoun}
                                            {confirmModal.action === 'ban' && (
                                                <> and Ban User</>
                                            )}
                                        </Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <p>
                                            Are you sure you want to{' '}
                                            <strong>
                                                {confirmModal.action === 'hide'
                                                    ? 'hide'
                                                    : 'permanently delete'}
                                            </strong>{' '}
                                            the {contentNoun} &ldquo;
                                            {confirmModal.postTitle}
                                            &rdquo;
                                            {confirmModal.action === 'ban' && (
                                                <>
                                                    {' '}
                                                    and ban{' '}
                                                    <strong>
                                                        {
                                                            confirmModal.postAuthor
                                                        }
                                                    </strong>
                                                </>
                                            )}
                                            ?
                                        </p>
                                        {confirmModal.action === 'ban' && (
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="ban-reason"
                                                    className="form-label small fw-medium"
                                                >
                                                    Ban reason{' '}
                                                    <span className="text-secondary">
                                                        (optional)
                                                    </span>
                                                </label>
                                                <textarea
                                                    id="ban-reason"
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Describe why this user is being banned..."
                                                    value={confirmModal.reason}
                                                    onChange={(e) =>
                                                        setConfirmModal(
                                                            (prev) => ({
                                                                ...prev,
                                                                reason: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                />
                                                <p className="text-danger small mt-3">
                                                    {isReplyReport
                                                        ? 'This action deletes the reply and bans the author until they are manually unbanned.'
                                                        : 'This action marks the post as deleted, decrements the community post count, and bans the author until they are manually unbanned.'}
                                                </p>
                                            </div>
                                        )}
                                        {confirmModal.action === 'approve' && (
                                            <p className="text-secondary small mb-0">
                                                The post will be published and
                                                visible in its community.
                                            </p>
                                        )}
                                        {confirmModal.action === 'hide' && (
                                            <p className="text-secondary small mb-0">
                                                Hidden posts are removed from
                                                public listings but preserved in
                                                the database.
                                            </p>
                                        )}
                                        {confirmModal.action === 'delete' && (
                                            <p className="text-danger small mb-0">
                                                {isReplyReport
                                                    ? 'This action permanently deletes the reply.'
                                                    : 'This action marks the post as deleted and decrements the community post count.'}
                                            </p>
                                        )}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button
                                            variant="outline-dark"
                                            onClick={closeConfirmModal}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant={
                                                confirmModal.action === 'hide'
                                                    ? 'warning'
                                                    : 'danger'
                                            }
                                            onClick={handleModerate}
                                        >
                                            {confirmModal.action === 'hide'
                                                ? `Hide ${ContentNoun}`
                                                : confirmModal.action === 'ban'
                                                  ? `Delete ${ContentNoun} & Ban User`
                                                  : `Delete ${ContentNoun}`}
                                        </Button>
                                    </Modal.Footer>
                                </>
                            )}
                        </Modal>
                    </>
                )}
            </Container>
        </main>
    );
}
